import { EditableProTable } from '@ant-design/pro-components'
import { Button, Form, Typography } from 'antd'
import FormSection from 'components/form-section'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import classes from './EnumerationTable.module.scss'
import { isNumeric } from 'utils/string'
import { isNil } from 'lodash'

const units = {
  PRESSURE: {
    FIELD: '(psia)',
    METRIC: '(bar)'
  },
  RS: {
    FIELD: '(Mscf/STB)',
    METRIC: '(m3/m3)'
  },
  RV: {
    FIELD: '(STB/MSCF)',
    METRIC: '(sm3/sm3)'
  }
}

const EnumerationTable = ({ fields, operation }) => {
  const form = Form.useFormInstance()

  const simulationUnit = Form.useWatch(["GENERAL", "unit"])
  const fluidType = Form.useWatch(["GENERAL", "fluid"])

  const [data, setData] = useState([])
  const [errors, setErrors] = useState([])

  // Add effect to handle RV field when fluid type changes
  useEffect(() => {
    if (fluidType === 'CONDENSATE') {
      // Update form values to include RV
      fields.forEach((field) => {
        const currentValue = form.getFieldValue(["INITIAL", field.name])
        if (!currentValue?.RV) {
          form.setFieldValue(["INITIAL", field.name], {
            ...currentValue,
            RV: null
          })
        }
      })
      fetchData()
    }
  }, [fluidType, fields, form])

  const fetchData = () => {
    // Limit to first 11 rows
    const limitedFields = fields.slice(0, 12)
    setData(limitedFields.map((field, index) => ({
      ...form.getFieldValue(["INITIAL", field.name]),
      ...(fluidType === 'CONDENSATE' && { RV: form.getFieldValue(["INITIAL", field.name])?.RV ?? null }),
      key: index
    })))
  }

  useEffect(() => {
    validateData(data)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const columns = useMemo(
    () => {
      const baseColumns = [
        {
          key: 'REGION',
          dataIndex: 'REGION',
          title: 'Region',
          editable: false
        },
        {
          key: 'PRESSURE',
          dataIndex: 'PRESSURE',
          title: `Pressure ${units.PRESSURE[simulationUnit] ?? ''}`,
          editable: true,
        }
      ]

      // Add SatGas for BLACK_OIL and CONDENSATE
      if (fluidType !== 'GAS') {
        baseColumns.push({
          key: 'SATGAS',
          dataIndex: 'SATGAS',
          title: 'SatGas',
          editable: true,
        })
      }

      // Add SatWater for all fluid types
      baseColumns.push({
        key: 'SATWATER',
        dataIndex: 'SATWATER',
        title: 'SatWater',
        editable: true,
      })

      // Add Rs for all fluid types
      if (fluidType !== 'GAS') {
        baseColumns.push({
          key: 'RS',
          dataIndex: 'RS',
          title: `Rs ${units.RS[simulationUnit] ?? ''}`,
          editable: true,
        })
      }

      // Add Rv for CONDENSATE only
      if (fluidType === 'CONDENSATE') {
        baseColumns.push({
          key: 'RV',
          dataIndex: 'RV',
          title: `Rv ${units.RV[simulationUnit] ?? ''}`,
          editable: true,
        })
      }

      baseColumns.push({
        title: "Operation",
        valueType: "option",
        key: "operation",
        width: 160,
        render: (text, record, index, action) => [
          <Button
            key="editable"
            size="small"
            onClick={() => {
              action?.startEditable?.(record.REGION);
            }}
          >
            Edit
          </Button>
        ],
      })

      return baseColumns
    }, [simulationUnit, fluidType])

  const validateData = useCallback((_data = []) => {
    setErrors([])

    const _errors = []

    const isRowEmpty = (row) => {
      const fieldsToCheck = ['PRESSURE', 'SATWATER']
      if (fluidType !== 'GAS') fieldsToCheck.push('SATGAS', 'RS')
      if (fluidType === 'CONDENSATE') fieldsToCheck.push('RV')
      return fieldsToCheck.every(field => isNil(row[field]))
    }

    if (_data.every(isRowEmpty)) return;

    _data.filter((row) => !isRowEmpty(row)).forEach((row, index) => {
      if (!isNumeric(row.PRESSURE)) {
        _errors.push(`Value of Pressure in row #${index + 1} must be a number`)
      } else {
        const pressure = parseFloat(row.PRESSURE)
        if (pressure < 0) {
          _errors.push(`Value of Pressure in row #${index + 1} must be >= 0`)
        }
      }

      if (fluidType !== 'GAS') {
        if (!isNumeric(row.SATGAS)) {
          _errors.push(`Value of SatGas in row #${index + 1} must be a number`)
        } else {
          const satgas = parseFloat(row.SATGAS)
          if (satgas < 0) {
            _errors.push(`Value of SatGas in row #${index + 1} must be >= 0`)
          }
        }

        if (!isNumeric(row.RS)) {
          _errors.push(`Value of RS in row #${index + 1} must be a number`)
        } else {
          const rs = parseFloat(row.RS)
          if (rs < 0) {
            _errors.push(`Value of RS in row #${index + 1} must be >= 0`)
          }
        }
      }

      if (!isNumeric(row.SATWATER)) {
        _errors.push(`Value of SatWater in row #${index + 1} must be a number`)
      } else {
        const satwater = parseFloat(row.SATWATER)
        if (satwater < 0) {
          _errors.push(`Value of SatWater in row #${index + 1} must be >= 0`)
        }
      }

      if (fluidType === 'CONDENSATE') {
        if (!isNumeric(row.RV)) {
          _errors.push(`Value of RV in row #${index + 1} must be a number`)
        } else {
          const rv = parseFloat(row.RV)
          if (rv < 0) {
            _errors.push(`Value of RV in row #${index + 1} must be >= 0`)
          }
        }
      }
    })

    setErrors(_errors)
  }, [fluidType])

  const limitedOperation = useMemo(() => ({
    ...operation,
    add: (...args) => {
      if (fields.length >= 12) {
        return; // Don't add if already at max
      }
      operation.add(...args);
    }
  }), [operation, fields.length]);

  return (
    <FormSection title="Enumeration">
      <EditableProTable
        options={false}
        pagination={false}
        search={false}
        columns={columns}
        value={data}
        rowKey="REGION"
        className={classes.tableContainer}
        cardProps={{
          className: classes.card
        }}
        scroll={{
          y: 380
        }}
        tableClassName={classes.table}
        tableExtraRender={() => (
          <ul className="px-24 text-danger">
            {errors.map(errMsg => (
              <li key={errMsg}>
                <Typography.Text type="danger">{errMsg}</Typography.Text>
              </li>
            ))}
          </ul>
        )}
        editable={{
          type: 'multiple',
          actionRender: (row, config, defaultDom) => [
            defaultDom.save,
            defaultDom.cancel,
          ],
          onSave: (rowKey, data, row) => {
            const { index, ...updatedRow } = data
            form.setFieldValue(["INITIAL", index], updatedRow)
            fetchData()
          }
        }}
        operation={limitedOperation}
        recordCreatorProps={{
          style: {
            display: fields.length >= 12 ? 'none' : undefined
          }
        }}
      />
    </FormSection>
  )
}

export default EnumerationTable